import { ColumnSort } from "@tanstack/react-table";
import { getEndpointPartnerEventCodes } from "@theprelude/core";
import { ControlCategories } from "@theprelude/sdk";
import { useMemo, useState } from "react";
import { getColumnVisibility, sortByField } from "@/components/table/columns";
import { InfiniteTable } from "@/components/table/infinite-table";
import { useInfiniteEndpoints } from "@/hooks/use-infinite-endpoints";
import { useTable } from "../../table/hooks";
import {
  formatSCMEndpointData,
  SCMEndpointEntry,
  useEndpointControls,
  useSCMEndpointColumns,
} from "./scm-columns";
import { SCMEmptyState } from "./scm-table-empty-state";

const getRowId = (endpoint: SCMEndpointEntry) => endpoint.endpoint_id;

const controlCategories = [
  ControlCategories.XDR,
  ControlCategories.ASSET_MANAGER,
  ControlCategories.VULN_MANAGER,
];

export const PrimaryUserEndpointsTable = ({ userId }: { userId: string }) => {
  const [columnSort, setColumnSort] = useState<ColumnSort[]>([]);

  const endpointControls = useEndpointControls();

  const columns = useSCMEndpointColumns({
    endpointControls,
    noControlEventCodes: getEndpointPartnerEventCodes(undefined),
    controlCategories,
  });

  const query = useMemo(() => {
    const sortedColumn = columns.find((c) => c.id === columnSort[0]?.id);
    return {
      filter: {
        instances: { any: { primary_user_id: userId } },
      },
      orderBy: sortByField(columnSort[0], sortedColumn?.meta?.odata),
    };
  }, [columns, columnSort, userId]);

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useInfiniteEndpoints({ query });

  const endpoints = useMemo(
    () =>
      formatSCMEndpointData({
        endpoints: data?.data ?? [],
        controlCategories,
        endpointControls,
      }),
    [data, endpointControls],
  );

  const table = useTable({
    name: "all-endpoints",
    data: endpoints,
    columns,
    manualSorting: true,
    manualFiltering: true,
    initialState: {
      columnVisibility: getColumnVisibility(columns),
    },
    state: {
      sorting: columnSort,
    },
    onSortingChange: setColumnSort,
    getRowId,
  });

  return (
    <InfiniteTable
      table={table}
      loading={isLoading}
      isFetching={isFetching}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      height={235}
      emptyState={<SCMEmptyState resourceType="endpoint" />}
    />
  );
};
